import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeftRight, Ban, Plus, Search as SearchIcon } from "lucide-react";

import { searchMatchCodes } from "../../../services/tendersApi";

/* "Industries you bid in" — Sensa suggests, the person decides.
 *
 * Every suggested code is a row to keep or untick. Unticking only stops Sensa
 * searching that code; a tender filed under it can still match on its words.
 * "Never match" is the strong version, asked for separately. Swap offers the
 * codes next to one (with how many open tenders each has), and more can be added
 * from "You might also bid in" or by searching every official code by name.
 */

const fmt = (n) => (n == null ? "" : `${n.toLocaleString()} open`);

function CodeRow({ row, onToggle, onSwap, onNever, swapping }) {
  return (
    <div className={`flex items-center gap-3 px-3 py-2.5 ${row.selected ? "" : "bg-[#18101C]/[0.02]"}`}>
      <input
        type="checkbox"
        checked={row.selected}
        onChange={() => onToggle(row)}
        aria-label={`Search ${row.code}`}
        className="h-4 w-4 flex-none cursor-pointer accent-[#A04A97]"
      />
      <div className={`min-w-0 flex-1 ${row.selected ? "" : "opacity-60"}`}>
        <p className="truncate text-[14.5px] text-[#18101C]">
          <b className="font-semibold">{row.code}</b>
          {row.title && <span> · {row.title}</span>}
        </p>
        <p className="text-[12.5px] text-[#77707E]">
          {[row.scheme, fmt(row.open_count), row.from === "memory" ? "Suggested from Memory" : "Added by you"]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
      <button
        type="button"
        onClick={() => onSwap(row)}
        aria-expanded={swapping}
        title="Swap for a closer code"
        className={`inline-flex h-8 items-center gap-1 rounded-lg px-2.5 text-[13px] font-semibold transition-colors ${
          swapping ? "bg-[#A04A97]/10 text-[#8E4187]" : "text-[#6B6472] hover:bg-[#18101C]/[0.05] hover:text-[#18101C]"
        }`}
      >
        <ArrowLeftRight className="h-3.5 w-3.5" strokeWidth={2.2} />
        Swap
      </button>
      <button
        type="button"
        onClick={() => onNever(row)}
        title="Never match tenders filed under this code"
        aria-label={`Never match ${row.code}`}
        className="grid h-8 w-8 place-items-center rounded-lg text-[#9CA3AF] transition-colors hover:bg-[#FEF2F2] hover:text-[#B91C1C]"
      >
        <Ban className="h-3.5 w-3.5" strokeWidth={2.2} />
      </button>
    </div>
  );
}

function Option({ row, onPick, label = "Add" }) {
  return (
    <button
      type="button"
      onClick={() => onPick(row)}
      className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-[#A04A97]/[0.06]"
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] text-[#18101C]">
          <b className="font-semibold">{row.code}</b>
          {row.title && <span> · {row.title}</span>}
        </span>
        <span className="block text-[12.5px] text-[#77707E]">
          {[row.scheme, fmt(row.open_count), row.why].filter(Boolean).join(" · ")}
        </span>
      </span>
      <span className="flex-none text-[13px] font-semibold text-[#8E4187]">{label}</span>
    </button>
  );
}

export default function IndustryPicker({ profile, draft, onChange }) {
  const [added, setAdded] = useState({}); // codes picked this session: code -> row
  const [swapFor, setSwapFor] = useState(null);
  const [swapOptions, setSwapOptions] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);

  const rows = useMemo(() => {
    const known = new Map(profile.codes.map((c) => [c.code, c]));
    return [...profile.codes, ...Object.values(added).filter((r) => !known.has(r.code))]
      .filter((r) => !draft.exclude_codes.includes(r.code))
      .map((r) => {
        const fromMemory = r.from === "memory";
        return {
          ...r,
          from: fromMemory ? "memory" : "you",
          selected: fromMemory ? !draft.skip_codes.includes(r.code) : draft.include_codes.includes(r.code),
        };
      });
  }, [profile, added, draft]);

  const listed = new Set(rows.map((r) => r.code));
  const related = (profile.related_codes || []).filter((r) => !listed.has(r.code));

  const without = (list, v) => list.filter((x) => x !== v);
  const withOne = (list, v) => (list.includes(v) ? list : [...list, v]);

  const select = (code, on, patch = {}) => {
    const memory = profile.codes.some((c) => c.code === code && c.from === "memory");
    const skip = patch.skip_codes || draft.skip_codes;
    const include = patch.include_codes || draft.include_codes;
    return memory
      ? { ...patch, skip_codes: on ? without(skip, code) : withOne(skip, code) }
      : { ...patch, include_codes: on ? withOne(include, code) : without(include, code) };
  };

  const toggle = (row) => onChange(select(row.code, !row.selected));

  const add = (row) => {
    setAdded((a) => ({ ...a, [row.code]: { ...row, from: "you" } }));
    onChange(select(row.code, true, { exclude_codes: without(draft.exclude_codes, row.code) }));
  };

  const swap = (from, to) => {
    setAdded((a) => ({ ...a, [to.code]: { ...to, from: "you" } }));
    const patch = select(to.code, true, { exclude_codes: without(draft.exclude_codes, to.code) });
    onChange(select(from.code, false, patch));
    setSwapFor(null);
  };

  const never = (row) =>
    onChange({
      exclude_codes: withOne(draft.exclude_codes, row.code),
      include_codes: without(draft.include_codes, row.code),
      skip_codes: without(draft.skip_codes, row.code),
    });

  // The codes next to the one being swapped, with how many open tenders each has.
  useEffect(() => {
    if (!swapFor) return undefined;
    const controller = new AbortController();
    setSwapOptions(null);
    searchMatchCodes({ near: swapFor }, { signal: controller.signal })
      .then((r) => setSwapOptions(r.filter((x) => !listed.has(x.code)).slice(0, 8)))
      .catch(() => setSwapOptions([]));
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swapFor]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults(null);
      return undefined;
    }
    const controller = new AbortController();
    const t = setTimeout(() => {
      searchMatchCodes({ q }, { signal: controller.signal })
        .then(setResults)
        .catch(() => {});
    }, 300);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [query]);

  return (
    <div className="mb-7">
      <p className="text-[15px] font-semibold text-[#18101C]">Industries you bid in</p>
      <p className="mb-2.5 mt-0.5 text-[13.5px] leading-relaxed text-[#6B6472]">
        Sensa suggested these from your Memory. Keep the ones you bid in, swap one for a closer code, or add more.
        Unticking only stops Sensa searching that code.
      </p>

      <div className="divide-y divide-[#18101C]/[0.06] overflow-hidden rounded-xl border border-[#18101C]/10 bg-white">
        {rows.length ? (
          rows.map((row) => (
            <div key={row.code}>
              <CodeRow
                row={row}
                swapping={swapFor === row.code}
                onToggle={toggle}
                onSwap={(r) => setSwapFor((cur) => (cur === r.code ? null : r.code))}
                onNever={never}
              />
              {swapFor === row.code && (
                <div className="border-t border-dashed border-[#A04A97]/20 bg-[#FBF2FA]/60 py-1">
                  <p className="px-3 pb-0.5 pt-1.5 text-[12px] font-semibold uppercase tracking-wide text-[#8E4187]">
                    Closer codes to {row.code}
                  </p>
                  {swapOptions === null ? (
                    <p className="px-3 py-2 text-[13.5px] text-[#77707E]">Looking…</p>
                  ) : swapOptions.length ? (
                    swapOptions.map((o) => <Option key={o.code} row={o} label="Use this" onPick={(to) => swap(row, to)} />)
                  ) : (
                    <p className="px-3 py-2 text-[13.5px] text-[#77707E]">No nearby codes have open tenders.</p>
                  )}
                </div>
              )}
            </div>
          ))
        ) : (
          <p className="px-3 py-3 text-[14px] text-[#77707E]">
            No codes suggested yet. They're worked out on the first match run; add yours below.
          </p>
        )}
      </div>

      {!!related.length && (
        <div className="mt-3">
          <p className="mb-2 text-[14px] font-semibold text-[#18101C]">You might also bid in</p>
          <div className="flex flex-wrap gap-1.5">
            {related.map((r) => (
              <button
                key={r.code}
                type="button"
                onClick={() => add(r)}
                title={r.why}
                className="inline-flex max-w-full items-center gap-1 rounded-lg border border-dashed border-[#A04A97]/35 bg-white px-3 py-1.5 text-[13.5px] text-[#7A3A73] transition-colors hover:border-[#A04A97] hover:bg-[#FBF2FA]"
              >
                <Plus className="h-3 w-3 flex-none" strokeWidth={2.6} />
                <b className="font-semibold">{r.code}</b>
                <span className="truncate">· {r.title}</span>
                <span className="flex-none text-[#77707E]">· {fmt(r.open_count)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="relative mt-3">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9CA3AF]" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search all NAICS / PSC codes by number or name"
          className="h-10 w-full rounded-lg border border-[#18101C]/15 bg-white pl-9 pr-3 text-[14px] text-[#18101C] outline-none placeholder:text-[#9CA3AF] focus:border-[#A04A97]"
        />
        {results && (
          <div className="mt-1 max-h-[220px] overflow-y-auto rounded-xl border border-[#18101C]/10 bg-white py-1 shadow-sm">
            {results.filter((r) => !listed.has(r.code)).length ? (
              results
                .filter((r) => !listed.has(r.code))
                .map((r) => (
                  <Option
                    key={r.code}
                    row={r}
                    onPick={(row) => {
                      add(row);
                      setQuery("");
                    }}
                  />
                ))
            ) : (
              <p className="px-3 py-2 text-[13.5px] text-[#77707E]">No other codes match.</p>
            )}
          </div>
        )}
      </div>

      {!!draft.exclude_codes.length && (
        <div className="mt-3">
          <p className="mb-2 text-[14px] font-semibold text-[#18101C]">Never matched</p>
          <div className="flex flex-wrap gap-1.5">
            {draft.exclude_codes.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => onChange({ exclude_codes: without(draft.exclude_codes, c) })}
                title="Match this code again"
                className="inline-flex items-center gap-1 rounded-lg border border-dashed border-[#18101C]/15 px-3 py-1.5 text-[13.5px] text-[#77707E] hover:border-[#A04A97]/40 hover:text-[#18101C]"
              >
                <Ban className="h-3 w-3" strokeWidth={2.2} />
                {c}
                <span className="font-semibold text-[#8E4187]">Undo</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
